import { StepHeader } from "./components/StepHeader";
import React, { useState, useEffect, useCallback } from "react";
import { FullscreenModal } from "./components/FullscreenModal";
import { useSteps } from "./useSteps";
import { useWarehouse } from "./useWarehouse";
import { useWarehouseContext } from "./WarehouseProvider";
import { CapturePackagePhotos } from "./CapturePackagePhotos";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "components/button";
import { ReactComponent as WarningSvg } from "./svgs/warning.svg";
import checkContentsIcon from "./images/check-contents-icon.png";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

const TITLE = "Check Contents";

export const CheckContents = () => {
  const { next, reject, jumpTo } = useSteps();
  const { setHasCheckedContents } = useWarehouse();
  const { packagePictures } = useWarehouseContext();
  const [showModal, setShowModal] = useState(false);

  const handleRejectClick = () => {
    if (packagePictures.length === 0) {
      setShowModal(true);
    } else {
      reject();
    }
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleAddPhotos = () => {
    jumpTo(CapturePackagePhotos);
  };

  const handleContinue = useCallback(() => {
    setHasCheckedContents(true);
    next();
  }, [setHasCheckedContents, next]);

  // Handle Enter key to trigger Continue
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !showModal) {
        e.preventDefault();
        handleContinue();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, handleContinue]);

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <StepHeader />

        <div className="flex-1 flex flex-col items-center pt-[120px]">
          <div className="flex flex-col items-center gap-4">
            <img
              src={checkContentsIcon}
              alt="Check Contents"
              className="w-[168px] h-[168px]"
            />
            <div className="flex flex-col items-center gap-[6px]">
              <h1 className="text-[29px] font-bold text-white leading-[37px] tracking-[-0.01em]">
                {TITLE}
              </h1>
              <p className="text-[20px] font-medium text-[#C6CCE8] leading-[26px] text-center max-w-[600px]">
                Open the box and do a scan of the contents. If you estimate more
                than half the box is non-conform, reject.
              </p>
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter>
          <WarehouseButtonGroup>
            <WarehouseButton variant="reject" onClick={handleRejectClick}>
              Reject
            </WarehouseButton>
            <WarehouseButton variant="primary" onClick={handleContinue}>
              Continue
            </WarehouseButton>
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>

      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              className="fixed inset-0 bg-black z-30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              className="fixed inset-0 flex items-center justify-center z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="bg-[#030711] p-6 rounded-xl shadow-lg w-full max-w-lg max-h-[95vh] overflow-auto z-20 border border-[#282F44]">
                <div className="flex flex-row items-center gap-2 mb-4">
                  <WarningSvg
                    style={{
                      width: "32px",
                      height: "32px",
                      color: "#D8A924",
                    }}
                  />
                  <h2 className="text-[24px] text-white font-bold">
                    No Package Photo
                  </h2>
                </div>
                <p className="text-[18px] text-[#8086A0] font-light mb-6">
                  Please add Package Photo before rejecting the package.
                </p>
                <div className="flex justify-between gap-6">
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    className="w-full border-2 rounded-xl text-[21px] font-normal h-[72px] p-6 border-[#282F44] text-[#DAE3F9] bg-transparent hover:bg-[#282F44]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddPhotos}
                    className="w-full rounded-xl text-[21px] p-6 bg-[#173991] hover:bg-[#2256DB]"
                  >
                    Add Photos
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </FullscreenModal>
  );
};

CheckContents.title = TITLE;
